import React from 'react'
import styled from 'styled-components'
import Modal from '@jarvis.oddle/core/components/Modal'
import Flex from '@jarvis.oddle/core/components/Flex'
import { isObjectEmpty, shallowEqual } from 'src/utils/object'
import { P, H3, H4, LinkText } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import usePrevious from '@jarvis.oddle/core/hooks/usePrevious'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Divider from '@jarvis.oddle/core/components/Divider'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import {
  colors,
  paddingSizes,
  marginSizes,
  borderRadius,
  borderRadiusSizes,
  dropShadows,
  dropShadowVariations,
  iconSizes
} from '@jarvis.oddle/core/theme/default'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import IconTrackLocation from '@jarvis.oddle/core/components/Icons/IconTrackLocation'
import IconEdit from '@jarvis.oddle/core/components/Icons/IconEdit'
import { setAlpha } from '@jarvis.oddle/core/tools/style'
import MapService, { mapServices } from 'src/containers/MapService'
import StoreInformationCard from './StoreInformationCard'
import { sortAvailableStores } from './helper'

const EditButtonContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  width: 48px;
  height: 48px;
`

const UseLocationContainer = styled(DivButton).attrs(() => ({
  marginTop: marginSizes.small
}))`
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  display: flex;
  align-self: flex-start;
  align-items: center;
`

const RadioContainer = styled(Flex).attrs(() => ({
  alignItems: 'center',
  paddingTop: paddingSizes.small,
  paddingBottom: paddingSizes.small,
  paddingLeft: paddingSizes.medium,
  paddingRight: paddingSizes.medium,
  marginTop: marginSizes.small,
  marginBottom: marginSizes.small
}))`
  border: 1px ${props => props.checked ? props.theme.buttonColor : setAlpha(props.theme.buttonColor, 0.5)} solid;
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  cursor: pointer;
  ${props => props.checked ? `box-shadow: ${dropShadows[dropShadowVariations.heavy]};` : ''}
`

const StorePicker = ({
  availableStores,
  mapServiceProps,
  onViewMapClick
}: {
  availableStores: Oms.V4.Store
  onViewMapClick: () => void
  mapServiceProps: {
    mapApi: {
      getCurrentAddress: () => Promise<Record<string, unknown>>
      findPlaceFromQuery: (query) => Promise<Record<string, unknown>>
    }
    MapServiceLogo: string
  }
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { values, setFieldValue } = useFormContext()

  const { userAddress } = values || {}
  const prevUserAddress = usePrevious(userAddress)

  const sortedAvailableStores = React.useMemo(() => {
    return sortAvailableStores({ availableStores, userAddress })
  }, [availableStores, userAddress])

  // show user location input when the cart has more than 5 stores
  const shouldShowLocationInput = (sortedAvailableStores || []).length > 5

  const { mapApi, MapServiceLogo } = mapServiceProps || {}

  const resultParser = x => x.formattedAddress

  const onEditClick = () => {
    setFieldValue('userAddress', null)
  }

  const searchApi = async () => {
    const coords = await mapApi.getCurrentAddress()

    if (isObjectEmpty(coords)) {
      showNotificationToast.error(t('Could not get your current location. Please try searching by address.'))
    }

    return coords
  }

  React.useEffect(() => {
    // always select the first index of the store every time user change his location
    if (!shallowEqual(userAddress || {}, prevUserAddress || {})) {
      const nextSelectedStoreId = (sortedAvailableStores || [])[0].id
      setFieldValue('selectedStoreId', nextSelectedStoreId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAddress])

  return (
    <React.Fragment>
      {shouldShowLocationInput && !isObjectEmpty(userAddress) && (
        <Modal.Section>
          <DivButton width='100%' onClick={onEditClick}>
            <Flex
              width='100%' flexDirection='row' justifyContent='space-between' alignItems='center'>
              <Flex
                flexGrow={1}
                flexDirection='column'
                justifyContent='center'
                alignItems='flex-start'>
                <H4
                  bold
                  normalised
                  marginBottom={marginSizes.small}>
                  {isObjectEmpty(userAddress) ? t('Search for nearby stores') : t('Stores near')}
                </H4>
                <P
                  flexHeight
                  normalised
                  numOfLines={2}>
                  {userAddress.formattedAddress}
                </P>
              </Flex>
              <EditButtonContainer>
                <IconEdit iconSize={iconSizes.medium} />
              </EditButtonContainer>
            </Flex>
          </DivButton>
        </Modal.Section>
      )}
      <Modal.Section>
        {shouldShowLocationInput && isObjectEmpty(userAddress) && (
          <React.Fragment>
            <H4 bold normalised marginBottom={marginSizes.small}>{t('Search for nearby stores')}</H4>
            <Form.AddressSearchInput
              name='userAddress'
              label={t('Enter an address to search for nearby stores')}
              resetOnUnmount={false}
              searchApi={mapApi.findPlaceFromQuery}
              resultParser={resultParser}
              MapServiceLogo={MapServiceLogo}
              initialSelected={userAddress || {}}>
              {({ loading, actions: searchActions }) => {
                const setCurrentAddress = async () => searchActions.searchAndSetSelected(searchApi)
                return (
                  <UseLocationContainer disabled={loading} onClick={setCurrentAddress}>
                    <IconTrackLocation fill={colors.alternativeHighlightColor} />
                    <P color={colors.alternativeHighlightColor} marginLeft='small' normalised>{t('Use current location')}</P>
                  </UseLocationContainer>
                )
              }}
            </Form.AddressSearchInput>
            <Divider marginTop={marginSizes.medium} marginBottom={marginSizes.medium} />
          </React.Fragment>
        )}
        {!shouldShowLocationInput && (
          <H3 bold normalised>{t('Select a Pickup Store')}</H3>
        )}
        <Flex justifyContent='space-between' marginTop={marginSizes.small} marginBottom={marginSizes.small}>
          <P bold normalised>
            {((sortedAvailableStores || []).length > 0
              ? t('_stores_ Store(s) Available', { stores: (sortedAvailableStores || []).length })
              : t('No Store Available')
            )}
          </P>
          <LinkText heading onClick={onViewMapClick}>{t('View on Map')}</LinkText>
        </Flex>
        <Form.RadioGroup name='selectedStoreId' resetOnUnmount={false}>
          {(sortedAvailableStores || []).map((store) => (
            <Form.Radio key={store.id} Container={RadioContainer} value={store.id}>
              {({ checked }) => (
                <StoreInformationCard
                  showInfo
                  selected={checked}
                  store={store}
                  showCollectionTime={checked} />
              )}
            </Form.Radio>
          ))}
        </Form.RadioGroup>
      </Modal.Section>
    </React.Fragment>
  )
}

export default (props: {
  availableStores
  onViewMapClick: () => void
}): JSX.Element => (
  <MapService mapService={mapServices.GOOGLE_MAP}>
    {(mapServiceProps) => <StorePicker mapServiceProps={mapServiceProps} {...props} />}
  </MapService>
)
